import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Outfit, Rarity, CharacterId } from '@/types'
import { GACHA_RATES, GACHA_PITY_THRESHOLD } from '@/lib/constants'

// Pre-defined outfits for each character
const ALL_OUTFITS: Outfit[] = [
  // Hana outfits
  { id: 'hana-default', characterId: 'hana', name: 'School Uniform', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: true },
  { id: 'hana-casual', characterId: 'hana', name: 'Casual Summer', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: false },
  { id: 'hana-sporty', characterId: 'hana', name: 'Gym Outfit', rarity: 'common', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'hana-cheerleader', characterId: 'hana', name: 'Cheerleader', rarity: 'rare', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'hana-swimsuit', characterId: 'hana', name: 'Beach Bikini', rarity: 'rare', contentLevel: 3, assetPath: '', isDefault: false },
  { id: 'hana-bunny', characterId: 'hana', name: 'Bunny Suit', rarity: 'ultra_rare', contentLevel: 4, assetPath: '', isDefault: false },

  // Yuki outfits
  { id: 'yuki-default', characterId: 'yuki', name: 'Student Council', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: true },
  { id: 'yuki-casual', characterId: 'yuki', name: 'Winter Fashion', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: false },
  { id: 'yuki-office', characterId: 'yuki', name: 'Office Lady', rarity: 'common', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'yuki-maid', characterId: 'yuki', name: 'Maid Outfit', rarity: 'rare', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'yuki-lingerie', characterId: 'yuki', name: 'Silk Lingerie', rarity: 'rare', contentLevel: 3, assetPath: '', isDefault: false },
  { id: 'yuki-dominatrix', characterId: 'yuki', name: 'Dark Queen', rarity: 'ultra_rare', contentLevel: 4, assetPath: '', isDefault: false },

  // Momo outfits
  { id: 'momo-default', characterId: 'momo', name: 'Cozy Sweater', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: true },
  { id: 'momo-casual', characterId: 'momo', name: 'Sundress', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: false },
  { id: 'momo-apron', characterId: 'momo', name: 'Naked Apron', rarity: 'common', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'momo-nurse', characterId: 'momo', name: 'Nurse Outfit', rarity: 'rare', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'momo-sleepwear', characterId: 'momo', name: 'Sheer Nightgown', rarity: 'rare', contentLevel: 3, assetPath: '', isDefault: false },
  { id: 'momo-bride', characterId: 'momo', name: 'Wedding Night', rarity: 'ultra_rare', contentLevel: 4, assetPath: '', isDefault: false },

  // Sumire outfits
  { id: 'sumire-default', characterId: 'sumire', name: 'Elegant Dress', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: true },
  { id: 'sumire-casual', characterId: 'sumire', name: 'Wine Evening', rarity: 'common', contentLevel: 1, assetPath: '', isDefault: false },
  { id: 'sumire-teacher', characterId: 'sumire', name: 'Strict Teacher', rarity: 'common', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'sumire-kimono', characterId: 'sumire', name: 'Silk Kimono', rarity: 'rare', contentLevel: 2, assetPath: '', isDefault: false },
  { id: 'sumire-gothic', characterId: 'sumire', name: 'Gothic Lolita', rarity: 'rare', contentLevel: 3, assetPath: '', isDefault: false },
  { id: 'sumire-dominatrix', characterId: 'sumire', name: 'Shadow Empress', rarity: 'ultra_rare', contentLevel: 4, assetPath: '', isDefault: false },
]

interface PullResult {
  outfit: Outfit
  isNew: boolean
  wasPity: boolean
}

interface GachaState {
  unlockedOutfitIds: string[]
  pullsSincePity: number
  totalPulls: number

  // Actions
  pull: () => PullResult
  multiPull: () => PullResult[]
  isOutfitUnlocked: (outfitId: string) => boolean
  getOutfitsForCharacter: (characterId: CharacterId) => Outfit[]
  getUnlockedOutfits: () => Outfit[]
  getAllOutfits: () => Outfit[]
}

// Get rarity based on rates and pity
const rollRarity = (pullsSincePity: number): Rarity => {
  // Pity system: guaranteed ultra rare at threshold
  if (pullsSincePity >= GACHA_PITY_THRESHOLD - 1) {
    return 'ultra_rare'
  }

  // Soft pity: increased rates after 40 pulls
  let ultraRareRate = GACHA_RATES.ultra_rare
  if (pullsSincePity >= 40) {
    ultraRareRate += (pullsSincePity - 40) * 0.02 // +2% per pull after 40
  }

  const roll = Math.random()

  if (roll < ultraRareRate) return 'ultra_rare'
  if (roll < ultraRareRate + GACHA_RATES.rare) return 'rare'
  return 'common'
}

// Get a random outfit of the specified rarity
const getRandomOutfit = (rarity: Rarity): Outfit => {
  const outfitsOfRarity = ALL_OUTFITS.filter((o) => o.rarity === rarity && !o.isDefault)

  if (outfitsOfRarity.length === 0) {
    // Fallback to any non-default outfit
    const nonDefault = ALL_OUTFITS.filter((o) => !o.isDefault)
    return nonDefault[Math.floor(Math.random() * nonDefault.length)]
  }

  return outfitsOfRarity[Math.floor(Math.random() * outfitsOfRarity.length)]
}

export const useGachaStore = create<GachaState>()(
  persist(
    (set, get) => ({
      // Start with default outfits unlocked
      unlockedOutfitIds: ALL_OUTFITS.filter((o) => o.isDefault).map((o) => o.id),
      pullsSincePity: 0,
      totalPulls: 0,

      pull: () => {
        const state = get()
        const rarity = rollRarity(state.pullsSincePity)
        const outfit = getRandomOutfit(rarity)
        const isNew = !state.unlockedOutfitIds.includes(outfit.id)
        const wasPity = state.pullsSincePity >= GACHA_PITY_THRESHOLD - 1

        set((state) => ({
          unlockedOutfitIds: isNew
            ? [...state.unlockedOutfitIds, outfit.id]
            : state.unlockedOutfitIds,
          pullsSincePity: rarity === 'ultra_rare' ? 0 : state.pullsSincePity + 1,
          totalPulls: state.totalPulls + 1,
        }))

        return { outfit, isNew, wasPity }
      },

      multiPull: () => {
        const results: PullResult[] = []
        for (let i = 0; i < 10; i++) {
          results.push(get().pull())
        }
        return results
      },

      isOutfitUnlocked: (outfitId) => get().unlockedOutfitIds.includes(outfitId),

      getOutfitsForCharacter: (characterId) =>
        ALL_OUTFITS.filter((o) => o.characterId === characterId),

      getUnlockedOutfits: () =>
        ALL_OUTFITS.filter((o) => get().unlockedOutfitIds.includes(o.id)),

      getAllOutfits: () => ALL_OUTFITS,
    }),
    {
      name: 'waifudo-gacha',
    }
  )
)
